import { MarketingDestination } from "./marketing.models";

type Command = (...args: unknown[]) => void;
interface MetaQueue extends Command {
  queue: unknown[][];
  callMethod?: Command;
  loaded?: boolean;
  version?: string;
  push?: Command;
}
interface MarketingWindow {
  fbq?: MetaQueue;
  _fbq?: MetaQueue;
  dataLayer?: unknown[];
  gtag?: Command;
}

export interface BrowserPixelEvent {
  destination: MarketingDestination;
  event: string;
  properties: Record<string, unknown>;
  eventId: string;
  contentEventId: number;
}

// Interchangeable SDK adapters; measurement, eligibility and deduplication stay in the facade.
export interface BrowserPixelAdapter {
  send(event: BrowserPixelEvent): boolean;
}

function installScript(document: Document, id: string, src: string): void {
  if (document.getElementById(id)) return;
  const script = document.createElement("script");
  script.id = id;
  script.async = true;
  script.src = src;
  document.head.appendChild(script);
}

export class MetaPixelAdapter implements BrowserPixelAdapter {
  private readonly initialized = new Set<string>();

  constructor(private readonly document: Document) {}

  send({
    destination,
    event,
    properties,
    eventId,
  }: BrowserPixelEvent): boolean {
    const config = destination.public_configuration || {};
    const pixel = config["pixel_id"] || destination.public_destination_id || "";
    if (!/^\d{5,30}$/.test(pixel)) return false;
    const win = this.document.defaultView as unknown as MarketingWindow;
    if (!win.fbq) {
      const queue = ((...args: unknown[]) =>
        queue.callMethod
          ? queue.callMethod(...args)
          : queue.queue.push(args)) as MetaQueue;
      queue.queue = [];
      queue.loaded = true;
      queue.version = "2.0";
      queue.push = queue;
      win.fbq = queue;
      win._fbq = queue;
    }
    if (!this.initialized.has(pixel)) {
      win.fbq("set", "autoConfig", false, pixel);
      win.fbq("init", pixel);
      this.initialized.add(pixel);
      installScript(
        this.document,
        "yes7-meta-sdk",
        "https://connect.facebook.net/en_US/fbevents.js",
      );
    }
    win.fbq("trackSingle", pixel, event, properties, { eventID: eventId });
    return true;
  }
}

export class GooglePixelAdapter implements BrowserPixelAdapter {
  private readonly initialized = new Set<string>();
  private readonly journeyNames: Record<string, string> = {
    ViewContent: "view_item",
    AddToCart: "add_to_cart",
    InitiateCheckout: "begin_checkout",
  };

  constructor(private readonly document: Document) {}

  send({
    destination,
    event,
    properties,
    eventId,
    contentEventId,
  }: BrowserPixelEvent): boolean {
    const config = destination.public_configuration || {};
    const conversionId = config["conversion_id"];
    const label = config["conversion_label"];
    if (
      !/^AW-\d{5,20}$/.test(conversionId) ||
      !/^[A-Za-z0-9_-]{1,128}$/.test(label)
    )
      return false;
    const win = this.document.defaultView as unknown as MarketingWindow;
    win.dataLayer ||= [];
    const layer = win.dataLayer;
    win.gtag ||= function (..._args: unknown[]) {
      // Google's bootstrap queue stores Arguments, not a custom command envelope.
      // eslint-disable-next-line prefer-rest-params
      layer.push(arguments);
    };
    if (!this.initialized.has(conversionId)) {
      win.gtag("js", new Date());
      win.gtag("config", conversionId, { send_page_view: false });
      installScript(
        this.document,
        "yes7-google-sdk",
        `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(conversionId)}`,
      );
      this.initialized.add(conversionId);
    }
    // Only the purchase action uses the configured conversion label.
    const purchase = event === "Purchase";
    win.gtag("event", purchase ? "conversion" : this.journeyNames[event], {
      ...properties,
      send_to: purchase ? `${conversionId}/${label}` : conversionId,
      ...(purchase ? { transaction_id: eventId } : {}),
      page_location: `${this.document.location.origin}/event/${contentEventId}`,
    });
    return true;
  }
}
